import { useEffect, useMemo, useCallback } from 'react';
import { formatDateToUTC } from '../../../lib/helpers';

import {
    useActiveMigrationsQuery,
    useLazyScheduledMigrationsQuery,
    useLazySuccessMigrationsQuery,
    useLazyErrorMigrationsQuery,
    useSourcesQuery,
    useAppliancesQuery,
    useEntitlementsQuery,
} from '../../../api';

export const useMigrationData = ({ userOrg }) => {
    const { today, weekFrom, monthFrom } = useMemo(() => {
        const now = new Date();
        return {
            today: formatDateToUTC(now),
            weekFrom: formatDateToUTC(new Date(now - 6 * 24 * 60 * 60 * 1000)),
            monthFrom: formatDateToUTC(new Date(now - 29 * 24 * 60 * 60 * 1000)),
        };
    }, []);

    const { data: activeMigrationsData, isLoading: isActiveLoading, isError: isActiveError } = useActiveMigrationsQuery(
        { userOrg },
        { skip: userOrg === null },
    );

    const [triggerSuccess, { data: successData, isLoading: isSuccessLoading, isError: isSuccessError }] =
        useLazySuccessMigrationsQuery();
    const [triggerError, { data: errorData, isLoading: isErrorLoading, isError: isErrorError }] =
        useLazyErrorMigrationsQuery();
    const [triggerScheduled, { data: scheduledData, isLoading: isScheduledPeriodLoading, isError: isScheduledError }] =
        useLazyScheduledMigrationsQuery();

    const periodDates = useMemo(
        () => ({
            day: { fromDate: today, toDate: today },
            week: { fromDate: weekFrom, toDate: today },
            month: { fromDate: monthFrom, toDate: today },
        }),
        [today, weekFrom, monthFrom],
    );

    useEffect(() => {
        if (userOrg) {
            triggerSuccess({ userOrg, ...periodDates.day });
            triggerError({ userOrg, ...periodDates.day });
            triggerScheduled({ userOrg, ...periodDates.day });
        }
    }, [userOrg, periodDates]);

    const fetchMigrationStatusForPeriod = useCallback(
        (period) => {
            if (!userOrg) return;
            const dates = periodDates[period];
            triggerSuccess({ userOrg, ...dates });
            triggerError({ userOrg, ...dates });
            triggerScheduled({ userOrg, ...dates });
        },
        [userOrg, periodDates, triggerSuccess, triggerError, triggerScheduled],
    );

    const { data: sourcesData, isLoading: isSourcesLoading, isError: isSourcesError } = useSourcesQuery(
        { userOrg }, 
        { skip: userOrg === null }
    );
    const { data: appliancesData, isLoading: isAppliancesLoading, isError: isAppliancesError } = useAppliancesQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const { data: entitlementsData, isLoading: isEntitlementsLoading, isError: isEntitlementsError } = useEntitlementsQuery(
        { userOrg },
        { skip: userOrg === null },
    );

    const migrationData = useMemo(() => {
        return {
            entitlements: {
                data: entitlementsData,
                isLoading: isEntitlementsLoading,
                isError: isEntitlementsError,
            },
            sources: {
                count: sourcesData?.count ?? 0,
                isLoading: isSourcesLoading,
                isError: isSourcesError,
            },
            appliances: {
                list: appliancesData?.content ?? [],
                isLoading: isAppliancesLoading,
                isError: isAppliancesError,
            },
            migrationStatus: {
                inProgress: {
                    count: activeMigrationsData?.count ?? 0,
                    isLoading: isActiveLoading,
                    isError: isActiveError,
                },
                current: {
                    scheduled: scheduledData?.count ?? 0,
                    completed: successData?.count ?? 0,
                    failed: errorData?.count ?? 0,
                    isLoading: isScheduledPeriodLoading || isSuccessLoading || isErrorLoading,
                    isError: isScheduledError || isSuccessError || isErrorError,
                },
                fetchMigrationStatusForPeriod,
            },
        };
    }, [
        entitlementsData,
        isEntitlementsLoading,
        isEntitlementsError,
        sourcesData,
        isSourcesLoading,
        isSourcesError,
        appliancesData,
        isAppliancesLoading,
        isAppliancesError,
        activeMigrationsData,
        isActiveLoading,
        isActiveError,
        scheduledData,
        successData,
        errorData,
        isScheduledPeriodLoading,
        isSuccessLoading,
        isErrorLoading,
        isScheduledError,
        isSuccessError,
        isErrorError,
        fetchMigrationStatusForPeriod,
    ]);

    return { migrationData };
};
